import { devLog, alwaysLog } from '@/utils/logger';
import { reactive } from 'vue';
import { GOOGLE_CONFIG, getOAuthFunctionUrl } from '@/config/google';
import { postTokenToDriveStreamSw } from '@/services/driveStreamSwBridge';

/* eslint-disable camelcase -- OAuth / Google token API 使用 snake_case 字段名 */
// Google API 类型声明
declare global {
  interface Window {
    google: {
      accounts: {
        oauth2: {
          revoke: (token: string) => void;
        };
      };
    };
  }
}

export interface GoogleUser {
  id: string
  email: string
  name: string
  picture: string
}

export interface AuthState {
  isAuthenticated: boolean
  user: GoogleUser | null
  accessToken: string | null
  tokenExpiry: number | null
  isLoading: boolean
  error: string | null
}

interface TokenPayload {
  access_token: string
  expires_in?: number
  refresh_token?: string
}

class GoogleAuthService {
  private authState = reactive<AuthState>({
    isAuthenticated: false,
    user: null,
    accessToken: null,
    tokenExpiry: null,
    isLoading: false,
    error: null
  });

  /** 不落 AuthState，避免无意暴露；与 accessToken 一并持久化 */
  private refreshToken: string | null = null;

  private gapiInitialized = false;

  private refreshInFlight: Promise<boolean> | null = null;

  /** 提前刷新：在过期前 2 分钟即视为过期 */
  private static readonly EXPIRY_SKEW_MS = 120_000;

  constructor() {
    this.initializeGoogleAPI();
  }

  getAuthState() {
    return this.authState;
  }

  private async initializeGoogleAPI() {
    try {
      await this.loadGoogleIdentityServices();
      this.gapiInitialized = true;
    } catch (error) {
      alwaysLog.error('Failed to initialize Google API:', error);
      this.authState.error = 'Failed to initialize Google API';
    }
  }

  private loadGoogleIdentityServices(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
      document.head.appendChild(script);
    });
  }

  async login() {
    if (!this.gapiInitialized) {
      this.authState.error = 'Google API not initialized';
      return;
    }

    this.authState.isLoading = true;
    this.authState.error = null;

    try {
      window.dispatchEvent(new CustomEvent('beforeLoginRedirect'));

      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.set('client_id', GOOGLE_CONFIG.CLIENT_ID);
      authUrl.searchParams.set('redirect_uri', GOOGLE_CONFIG.REDIRECT_URI);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', GOOGLE_CONFIG.SCOPES);
      authUrl.searchParams.set('include_granted_scopes', 'true');
      authUrl.searchParams.set('access_type', 'offline');
      authUrl.searchParams.set('prompt', 'consent');

      window.location.assign(authUrl.toString());
    } catch (error) {
      this.authState.error = 'Login failed';
      this.authState.isLoading = false;
    }
  }

  /**
   * OAuth 回调页：用授权码换 token 并完成用户信息拉取
   */
  async completeLoginWithAuthorizationCode(code: string): Promise<void> {
    const url = getOAuthFunctionUrl('google-oauth-exchange');
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code,
        redirect_uri: GOOGLE_CONFIG.REDIRECT_URI
      })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      alwaysLog.error('[GoogleAuthService] OAuth exchange failed', data);
      throw new Error(typeof data.error === 'string' ? data.error : 'oauth_exchange_failed');
    }
    this.applyTokenPayload(data as TokenPayload);
    await this.getUserInfo();
  }

  async ensureAuthenticated(): Promise<boolean> {
    if (this.authState.accessToken && !this.isTokenExpired()) {
      if (this.authState.user) {
        this.authState.isAuthenticated = true;
      }
      return true;
    }

    if (this.refreshToken && (!this.authState.accessToken || this.isTokenExpired())) {
      const refreshed = await this.tryRefreshAccessToken();
      if (refreshed) {
        if (!this.authState.user) {
          await this.getUserInfo();
        } else {
          this.authState.isAuthenticated = true;
          this.persistAuthSnapshot();
        }
        window.dispatchEvent(new CustomEvent('authStateChanged', { detail: this.authState }));
        return true;
      }
    }

    devLog.log('[GoogleAuthService] Token expired or not authenticated, initiating login...');
    this.login();
    return false;
  }

  private applyTokenPayload(data: TokenPayload) {
    this.authState.accessToken = data.access_token;
    const ei = data.expires_in;
    this.authState.tokenExpiry = ei
      ? Date.now() + Number(ei) * 1000
      : Date.now() + 3600 * 1000;
    if (data.refresh_token) {
      this.refreshToken = data.refresh_token;
    }
    // Drive 流式由 SW 持有 token；静默刷新/换 token 后必须推送，否则页面与 SW 长期不同步会导致 401/播放失败
    postTokenToDriveStreamSw(this.authState.accessToken);
  }

  private persistAuthSnapshot() {
    localStorage.setItem('googleAuth', JSON.stringify({
      user: this.authState.user,
      accessToken: this.authState.accessToken,
      tokenExpiry: this.authState.tokenExpiry,
      refreshToken: this.refreshToken
    }));
  }

  private async tryRefreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) {
      return false;
    }
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = (async() => {
      try {
        const url = getOAuthFunctionUrl('google-oauth-refresh');
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: this.refreshToken })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          devLog.warn('[GoogleAuthService] Refresh failed', data);
          return false;
        }
        const payload = data as TokenPayload;
        this.applyTokenPayload({
          access_token: payload.access_token,
          expires_in: payload.expires_in,
          refresh_token: payload.refresh_token || this.refreshToken || undefined
        });
        this.persistAuthSnapshot();
        return true;
      } catch (e) {
        alwaysLog.error('[GoogleAuthService] Refresh error', e);
        return false;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  private async getUserInfo() {
    devLog.log('Getting user info with token:', this.authState.accessToken ? 'Token available' : 'No token');

    if (!this.authState.accessToken) {
      this.authState.error = 'No access token available';
      this.authState.isLoading = false;
      return;
    }

    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          Authorization: `Bearer ${this.authState.accessToken}`
        }
      });

      devLog.log('User info response status:', response.status);

      if (!response.ok) {
        throw new Error(`Failed to get user info: ${response.status}`);
      }

      const userData = await response.json();
      devLog.log('User data received:', userData);

      this.authState.user = {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        picture: userData.picture
      };
      this.authState.isAuthenticated = true;
      this.authState.isLoading = false;

      devLog.log('Auth state updated:', this.authState);

      try {
        const logData = new URLSearchParams();
        logData.append('form-name', 'google-login-logs');
        logData.append('email', userData.email || 'unknown');
        logData.append('name', userData.name || 'unknown');
        logData.append('time', new Date().toLocaleString());
        logData.append('userAgent', navigator.userAgent);

        fetch('/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: logData.toString()
        }).catch(err => devLog.warn('Login logging failed', err));
      } catch (e) {
        // ignore logging errors
      }

      this.persistAuthSnapshot();

      window.dispatchEvent(new CustomEvent('authStateChanged', { detail: this.authState }));
    } catch (error) {
      alwaysLog.error('Error getting user info:', error);
      this.authState.error = 'Failed to get user info';
      this.authState.isLoading = false;
    }
  }

  logout() {
    const tokenToRevoke = this.authState.accessToken;
    postTokenToDriveStreamSw(null);

    this.authState.isAuthenticated = false;
    this.authState.user = null;
    this.authState.accessToken = null;
    this.authState.tokenExpiry = null;
    this.authState.error = null;
    this.refreshToken = null;

    localStorage.removeItem('googleAuth');

    if (tokenToRevoke && window.google?.accounts?.oauth2) {
      window.google.accounts.oauth2.revoke(tokenToRevoke);
    }
  }

  checkAuthStatus() {
    const savedAuth = localStorage.getItem('googleAuth');
    if (savedAuth) {
      try {
        const authData = JSON.parse(savedAuth);
        this.authState.user = authData.user;
        this.authState.accessToken = authData.accessToken;
        this.authState.tokenExpiry = authData.tokenExpiry;
        this.refreshToken = authData.refreshToken ?? null;
        this.authState.isAuthenticated = Boolean(authData.user && authData.accessToken);

        if (this.isTokenExpired()) {
          devLog.warn('Saved access token has expired or is near expiry; will refresh on next ensureAuthenticated');
        }
      } catch (error) {
        localStorage.removeItem('googleAuth');
      }
    }
  }

  getAccessToken(): string | null {
    if (this.authState.accessToken && this.isTokenExpired()) {
      devLog.warn('Access token has expired or is near expiry');
    }

    return this.authState.accessToken;
  }

  private isTokenExpired(): boolean {
    if (!this.authState.tokenExpiry) {
      return false;
    }

    return Date.now() >= this.authState.tokenExpiry - GoogleAuthService.EXPIRY_SKEW_MS;
  }

  async validateToken(): Promise<boolean> {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }

    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v1/tokeninfo', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * 从 OAuth 回调 URL 读取 ?code=（授权码流程）
   */
  parseAuthorizationCodeFromQuery(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      const clean = `${window.location.pathname}`;
      history.replaceState(null, '', clean);
    }
    return code;
  }

  /** Google 重定向错误 ?error= */
  parseOAuthErrorFromQuery(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return new URLSearchParams(window.location.search).get('error');
  }

  /**
   * Drive 等返回 401 时调用：有 refresh_token 则先静默刷新，否则全页登录
   */
  async recoverSessionAfterUnauthorized(): Promise<void> {
    if (this.refreshToken) {
      const ok = await this.tryRefreshAccessToken();
      if (ok) {
        window.dispatchEvent(new CustomEvent('authStateChanged', { detail: this.authState }));
        return;
      }
    }
    this.login();
  }

  /** Service Worker 在 Drive 返回 401 时调用：静默刷新 access_token（不改变页面路由） */
  async refreshAccessTokenSilently(): Promise<boolean> {
    return this.tryRefreshAccessToken();
  }
}

export const googleAuthService = new GoogleAuthService();

googleAuthService.checkAuthStatus();
